package com.htmlagent.site.state.brief

import com.htmlagent.site.i18n.BriefStrings

/**
 * The draft written out as the message the agent receives.
 *
 * It takes the shape the reference library uses — summary, style, structure, gesture, notes —
 * because that is the order the skills read a commission in: what it is before how it looks,
 * and how it looks before what moves.
 *
 * A block whose step was left open is **absent**, not empty. Sending "register: undecided"
 * would spend context telling the agent something it already assumes, and worse, it reads as an
 * instruction not to choose one.
 */
fun Brief.toPrompt(t: BriefStrings): String = listOfNotNull(
    // Only when something actually was decided: on a wholly skipped brief it would be a lie.
    if (BriefStep.entries.any { decided(it) }) t.promptDecided else null,
    if (directionSettled) t.promptDirectionSettled else null,
    summaryBlock(t),
    styleBlock(t),
    structureBlock(t),
    gestureBlock(t),
    referencesBlock(t),
    block(t.lblNotes, listOfNotNull(t.promptNotes, if (directionSettled) t.promptPresence else null)),
).joinToString("\n\n")

/**
 * Whether the four decisions a visual direction is made of all arrived.
 *
 * It is the gate for telling the agent not to load the skill that takes them, and it has to be
 * all four: with the register left open and the accent given, skipping that skill would leave
 * nobody deciding the register. That skill also carries the presence floor, which is why the
 * brief states it here — dropping it silently would make a fully-specified page flatter than a
 * vague one, which is exactly backwards.
 */
private val Brief.directionSettled: Boolean
    get() = decided(BriefStep.Register) && decided(BriefStep.Colour) && decided(BriefStep.Form)

/**
 * What was typed always travels, even with the step left open: it is the person's own words,
 * and it is the one thing the agent cannot infer.
 */
private fun Brief.summaryBlock(t: BriefStrings): String? {
    val typed = what.trim().ifBlank { null }
    if (!decided(BriefStep.Commission)) return typed
    val action = t.actionName(this.action.id)
    return block(
        t.lblSummary,
        listOfNotNull(
            "${t.kKind}: ${t.kindName(kind.id)} — ${t.kindLine(kind.id)}",
            typed,
            if (kind == PageKind.Landing) t.summarySingleAction(action) else t.summaryAction(action),
        ),
    )
}

private fun Brief.styleBlock(t: BriefStrings): String? = block(
    t.lblStyle,
    listOfNotNull(registerLine(t), typeLine(t)) + colourLines(t) + listOfNotNull(imageryLine(t), motionLine(t)),
)

private fun Brief.registerLine(t: BriefStrings): String? {
    if (!decided(BriefStep.Register)) return null
    val budget = register.budget
    val detail = listOf(
        t.accentCount(budget.accents),
        t.gestureBudget(budget.gestures),
        t.motionPhrase(t.motionName(budget.motion.id)),
        t.radiusCount(budget.radii),
        t.densityPhrase(t.densityName(budget.density.id)),
    ).joinToString(", ")
    return "${t.kRegister}: ${t.registerName(register.id)} — $detail"
}

private fun Brief.motionLine(t: BriefStrings): String? {
    if (!decided(BriefStep.Register)) return null
    return "${t.kMotion}: ${t.motionPhrase(t.motionName(register.budget.motion.id))}"
}

private fun Brief.typeLine(t: BriefStrings): String? {
    if (!decided(BriefStep.Form)) return null
    val pairing = typePairing
    val detail = "${pairing.display} ${t.typeDisplayRole}, ${pairing.body} ${t.typeBodyRole} — " +
        "${t.typeLoad}. ${t.typeFallback}: ${pairing.displayStack} / ${pairing.bodyStack}"
    return "${t.kType}: ${t.typeRule(detail)}"
}

/**
 * The three lines the colour step contributes.
 *
 * The contrast is stated as a measured number rather than left implicit: it is the one value the
 * agent cannot check from the hex alone, and the one a generated page gets wrong most often.
 */
private fun Brief.colourLines(t: BriefStrings): List<String> {
    if (!decided(BriefStep.Colour)) return emptyList()
    val seed = seed ?: return emptyList()
    val ramp = Palette.accentRamp(seed)
    val solid = ramp[SOLID].hex
    val onSolid = Palette.onSolid(solid, ramp[INK].hex)
    val ink = if (onSolid == Palette.WHITE) t.inkWhite else t.inkAccent
    return listOf(
        "${t.kAccent}: ${t.accentRule(solid)}",
        "${t.kNeutral}: ${t.neutralRule(t.chromaPhrase(Palette.neutralTint(seed).fixed(CHROMA_DECIMALS)))}",
        "${t.kText}: ${t.textRule("$ink, ${Palette.contrast(solid, onSolid).fixed(RATIO_DECIMALS)}:1")}",
        t.styleHue("${Palette.degrees(Palette.hue(solid) ?: seed.hue)}°"),
    )
}

private fun Brief.imageryLine(t: BriefStrings): String? {
    if (!decided(BriefStep.Form)) return null
    val detail = buildString {
        append(t.imageryPrompt(imagery.id))
        if (imagery.hasTreatment) append("; ").append(t.treatmentPrompt(treatment.id))
    }
    return "${t.kImagery}: ${t.imageryRule(detail)}"
}

private fun Brief.structureBlock(t: BriefStrings): String? =
    if (decided(BriefStep.Commission)) {
        block(t.lblStructure, sectionsOf(kind, t).mapIndexed { at, name -> "${at + 1}. $name" })
    } else {
        null
    }

private fun Brief.gestureBlock(t: BriefStrings): String? {
    if (!decided(BriefStep.Form) || gestures.isEmpty()) return null
    val detail = gestures.joinToString(" · ") { "${t.gestureName(it.id)} (${t.gestureKind(it.kind.id)})" }
    return block(t.lblGesture, listOf(t.gestureRule(detail)))
}

private fun Brief.referencesBlock(t: BriefStrings): String? =
    references.trim().ifBlank { null }?.let { block(t.lblReferences, listOf(it)) }

/** The section skeleton of each kind. A `when` and not a lookup: the list form takes no key. */
fun sectionsOf(kind: PageKind, t: BriefStrings): List<String> = when (kind) {
    PageKind.Landing -> t.sectionsLanding
    PageKind.Menu -> t.sectionsMenu
    PageKind.Event -> t.sectionsEvent
    PageKind.Portfolio -> t.sectionsPortfolio
    PageKind.Profile -> t.sectionsProfile
    PageKind.Waiting -> t.sectionsWaiting
}

private fun block(heading: String, lines: List<String>): String? =
    if (lines.isEmpty()) null else (listOf(heading) + lines).joinToString("\n")

/** Index of the solid inside a twelve-step ramp, and of the tone that may sit on top of it. */
private const val SOLID = 8
private const val INK = 11

private const val RATIO_DECIMALS = 2
private const val CHROMA_DECIMALS = 3

/** Kotlin/JS has no `String.format`; the platform's own rounding is the honest way to say it. */
private fun Double.fixed(decimals: Int): String = asDynamic().toFixed(decimals) as String
